import type { DashboardPromoCard } from 'api/dashboardPromoCards'
import type { CardT } from './Carousel'

export type PromoLinkTarget =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; url: string }

// Only web links are navigable. link_url comes from the promo CMS (semi-trusted
// merchant-admin input), so anything outside http(s) — javascript:, data:,
// vbscript:, etc. — must never reach window.open or the router.
const SAFE_PROTOCOLS = ['http:', 'https:']

// Decides how to follow a promo card's link, or null when the link is unsafe or
// unparseable (the card stays visible but does nothing on click). A same-origin
// dashboard link navigates in-app (client-side route, same tab); any other
// http(s) link opens in a new tab. Relative links have no origin of their own,
// so they resolve against the current origin and count as in-app routes.
export const resolvePromoLink = (linkUrl: string): PromoLinkTarget | null => {
  let parsed: URL
  try {
    parsed = new URL(linkUrl, window.location.origin)
  } catch {
    return null
  }
  if (!SAFE_PROTOCOLS.includes(parsed.protocol)) {
    return null
  }
  if (parsed.origin === window.location.origin) {
    return {
      kind: 'internal',
      path: `${parsed.pathname}${parsed.search}${parsed.hash}`,
    }
  }
  return { kind: 'external', url: linkUrl }
}

// Maps API promo cards to the carousel's card shape. The carousel renders an
// <img> per card, so cards without an image (null or empty string) are dropped
// rather than rendered as a broken image. Order is preserved (the API returns
// cards ordered by position).
export const toCarouselCards = (
  promoCards: DashboardPromoCard[],
  onClick: (linkUrl: string) => void,
  translateAlt: (position: number) => string
): CardT[] =>
  promoCards
    .filter((card): card is DashboardPromoCard & { imageUrl: string } =>
      Boolean(card.imageUrl)
    )
    .map(card => ({
      image: card.imageUrl,
      imageAlt: translateAlt(card.position),
      onClick: () => onClick(card.linkUrl),
    }))
